// @ts-nocheck
import React, { useCallback, useState } from 'react';
import { FormControl, Button, Box, Stack } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import PropTypes from 'prop-types';
import mime from 'mime';

import { convertFileToBase64 } from '../../../../utils/Utils';
import swalToast from '../../swalToast';
import PreviewTableChart from '../../ModelInventoryAndAssociation/PreviewTableChart';

/**
 * Iframe/Chart field component for uploading and displaying image-based charts
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|null} props.value - Current field value (base64 encoded image)
 * @param {Function} props.onChange - Function to handle value changes
 * @returns {React.ReactElement} The Iframe Field component
 */
const IframeFieldComponent = ({ value, onChange }) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [previewItem, setPreviewItem] = useState({});

  const UPLOAD_IMAGE = 'Upload Image';
  const CHANGE_IMAGE = 'Change Image';

  /**
   * Handles the change event for an image file input
   * @function
   * @param {Event} e - The change event object triggered by the file input
   * @returns {void}
   */
  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];

    // https://stackoverflow.com/questions/51724649/mime-type-of-file-returning-empty-in-javascript-on-some-machines
    const fType = mime.getType(file?.name);

    const pngFileType = 'image/png';

    const isFileTypePNG = [pngFileType].includes(file?.type)
      ? true
      : fType === pngFileType;

    if (file && isFileTypePNG) {
      convertFileToBase64(file)
        .then((base64String) => {
          onChange(base64String);
        })
        .catch((error) => {
          if (error) {
            swalToast({
              icon: 'error',
              title: 'Error reading image.',
            });
          }
        });
    } else {
      swalToast({
        icon: 'error',
        title: 'Image format not supported, please select PNG.',
      });
    }
  }, []);

  /**
   * Handles removing the uploaded image
   * @function
   * @returns {void}
   */
  const handleRemoveImage = useCallback(() => {
    onChange(null);
  }, [onChange]);

  const handleClosePreview = useCallback(() => {
    setOpenPreview(false);
    setPreviewItem({});
  }, [setOpenPreview, setPreviewItem]);

  return (
    <FormControl fullWidth>
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* <FormLabel sx={{ whiteSpace: 'nowrap' }}>{label}:</FormLabel> */}
        <Button
          variant="contained"
          component="label"
          title={value ? CHANGE_IMAGE : UPLOAD_IMAGE}
          size="small"
          sx={{
            fontSize: '12px',
            height: 1,
            minWidth: 2,
            whiteSpace: 'nowrap',
          }}
        >
          {value ? CHANGE_IMAGE : UPLOAD_IMAGE}
          <input
            type="file"
            accept="image/png"
            onChange={handleImageChange}
            style={{ display: 'none' }}
          />
        </Button>
      </Stack>

      {value?.length > 0 && (
        <Box
          mt={2}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <Box mb={1}>
            <Button
              color="primary"
              variant="outlined"
              onClick={handleRemoveImage}
              size="small"
              sx={{
                fontSize: '12px',
                height: 1,
                minWidth: 2,
                whiteSpace: 'nowrap',
              }}
              startIcon={<ClearIcon />}
            >
              Remove Image
            </Button>
          </Box>
          <Box
            sx={{
              border: (theme) => `1px solid ${theme?.palette?.divider}`,
              borderRadius: 1,
              overflow: 'hidden',
              maxWidth: '100%',
            }}
          >
            <img
              alt="Uploaded Chart"
              src={`data:image/png;base64,${value}`}
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </Box>
      )}
      <PreviewTableChart
        open={openPreview}
        handleClose={handleClosePreview}
        previewItem={previewItem}
      />
    </FormControl>
  );
};

IframeFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    is_editable: PropTypes.bool,
  }),
};

export default IframeFieldComponent;
